import { BayLevelEnum, ForeAftEnum, TContainerLengths } from "open-vessel-definition";
import { IBayPattern, IRowPattern, ITierPattern } from "../types/IPositionPatterns";

import { IContainerInformationExtended } from "../types/IContainerInformation";

export class CellInfo {
  row: IRowPattern;
  tier: ITierPattern;
  maxSize: TContainerLengths | undefined;
  restricted: 0 | 1 | undefined;
  isReefer: 1 | 0 | undefined;
  pairedBay: ForeAftEnum | undefined;
  sizes: Map<TContainerLengths, ICellCGsInfo> | undefined = undefined;

  constructor(dta: ICellSizesAndCgs) {
    this.row = dta.row;
    this.tier = dta.tier;
    this.restricted = dta.restricted;
    this.pairedBay = dta.pairedBay;
    this.isReefer = dta.isReefer;

    this.sizes = (Object.keys(dta.sizes || {}).map(Number) as TContainerLengths[]).reduce<
      Map<TContainerLengths, ICellCGsInfo>
    >((acc, size) => {
      if (dta.sizes?.[size]) acc.set(size, dta.sizes[size]);
      return acc;
    }, new Map<TContainerLengths, ICellCGsInfo>());

    this.maxSize = dta.maxSize;
  }

  has40Size(): boolean {
    return !!(this.maxSize !== undefined && this.maxSize >= 40);
  }

  getLcg(size: TContainerLengths): number | undefined {
    return this.sizes?.get(size)?.lcg;
  }

  getTcg(size: TContainerLengths): number | undefined {
    return this.sizes?.get(size)?.tcg;
  }

  getTcgAnySize(): number | undefined {
    return Array.from(this.sizes?.values() ?? []).find((size) => size.tcg !== undefined)?.tcg;
  }

  getBottomBase(size: TContainerLengths): number | undefined {
    return this.sizes?.get(size)?.bottomBase;
  }

  getCGs(size: TContainerLengths): {
    lcg: number | undefined;
    tcg: number | undefined;
    bottomBase: number | undefined;
  } {
    return {
      lcg: this.getLcg(size),
      tcg: this.getTcg(size),
      bottomBase: this.getBottomBase(size),
    };
  }
}

export type ICellSizesAndCgs =
  | {
      row: IRowPattern;
      tier: ITierPattern;
      pairedBay?: ForeAftEnum;
    } & (
      | {
          restricted: 0 | 1;
          maxSize?: never;
          isReefer?: never;
          sizes?: never;
        }
      | {
          restricted?: never;
          maxSize: TContainerLengths | undefined;
          isReefer?: 1 | 0;
          sizes: {
            [size in `${TContainerLengths}`]: ICellCGsInfo;
          };
        }
    );

export type ICellCGsInfo = {
  /** Size / Length of container */
  size: TContainerLengths;
  /** Longitudinal Center of Gravity */
  lcg?: number;
  /** Transversal Center of Gravity */
  tcg?: number;
  /** The bottom base of the tier of the specific cell */
  tierBottomBase?: number;
  /** The bottom base of the bay-level */
  bottomBase?: number;
  /** The bottom base of the bay-level with separation */
  bottomBaseWithSeparation?: number;
  /** The TIER that is at the bottom of the bay-lebel */
  bottomTier?: ITierPattern;
  /** If the bay is extending and to where */
  extending?: ForeAftEnum;
  /** The Odd Bay */
  isoOddBay: IBayPattern;
  /** Bay Level */
  bayLevel: BayLevelEnum;
  /** Container Information Extended */
  meta?: IContainerInformationExtended;
};
